import { useState, useEffect } from 'react';
import axios from 'axios';

const EMPTY_ARRAY: any[] = [];

// Mock API responses using local JSON files
const mockAPI = {
  get: async (url: string) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 700));
    
    try {
      // Map API endpoints to local JSON files
      const endpointMap: { [key: string]: string } = {
        '/api/dashboard/stats': '/src/data/dashboard.json',
        '/api/dashboard/performance': '/src/data/dashboard.json',
        '/api/dashboard/top-performers': '/src/data/dashboard.json',
        '/api/dashboard/rewards-by-type': '/src/data/dashboard.json',
        
        '/api/promoters': '/src/data/promoters.json',
        '/api/promoters/stats': '/src/data/promoters.json',
        
        '/api/prospects': '/src/data/prospects.json',
        '/api/prospects/stats': '/src/data/prospects.json',
        '/api/prospects/widget-submissions': '/src/data/prospects.json',
        
        '/api/meetings': '/src/data/meetings.json',
        
        '/api/reviews': '/src/data/reviews.json',
        '/api/reviews/external': '/src/data/reviews.json',
        '/api/reviews/stats': '/src/data/reviews.json',
        
        '/api/testimonials/our-requests': '/src/data/testimonials.json',
        '/api/testimonials/promoter-submissions': '/src/data/testimonials.json',
        '/api/testimonials/featured': '/src/data/testimonials.json',
        
        '/api/usecases/our-requests': '/src/data/usecases.json',
        '/api/usecases/promoter-submissions': '/src/data/usecases.json',
        '/api/usecases/published': '/src/data/usecases.json',
        '/api/usecases/featured': '/src/data/usecases.json',
        
        '/api/rewards/types': '/src/data/rewards.json',
        '/api/rewards/global': '/src/data/rewards.json',
        '/api/rewards/specific': '/src/data/rewards.json',
        '/api/rewards/payments': '/src/data/rewards.json',
        '/api/rewards/analytics': '/src/data/rewards.json',
        
        '/api/calls/our-requests': '/src/data/calls.json',
        '/api/calls/promoter-submissions': '/src/data/calls.json',
        '/api/calls/activated': '/src/data/calls.json',
      };

      const filePath = endpointMap[url];
      if (!filePath) {
        throw new Error(`No mock data found for ${url}`);
      }

      // Load JSON file
      const response = await fetch(filePath);
      if (!response.ok) {
        throw new Error(`Failed to load ${filePath}`);
      }
      
      const data = await response.json();
      
      // Extract specific data based on endpoint
      if (url.includes('/dashboard/stats')) {
        return { data: data.stats };
      } else if (url.includes('/dashboard/performance')) {
        return { data: data.performanceStats };
      } else if (url.includes('/dashboard/top-performers')) {
        return { data: data.topPerformers };
      } else if (url.includes('/dashboard/rewards-by-type')) {
        return { data: data.rewardsByType };
      } else if (url.includes('/promoters/stats')) {
        return { data: data.stats };
      } else if (url.includes('/promoters')) {
        return { data: data.promoters };
      } else if (url.includes('/prospects/stats')) {
        return { data: data.stats };
      } else if (url.includes('/prospects/widget-submissions')) {
        return { data: data.widgetSubmissions };
      } else if (url.includes('/prospects')) {
        return { data: data.prospects };
      } else if (url.includes('/meetings')) {
        return { data: data.meetings };
      } else if (url.includes('/reviews/external')) {
        return { data: data.externalReviews };
      } else if (url.includes('/reviews/stats')) {
        return { data: data.stats };
      } else if (url.includes('/reviews')) {
        return { data: data.reviews };
      } else if (url.includes('/testimonials/our-requests')) {
        return { data: data.ourRequests };
      } else if (url.includes('/testimonials/promoter-submissions')) {
        return { data: data.promoterSubmissions };
      } else if (url.includes('/testimonials/featured')) {
        return { data: data.featured };
      } else if (url.includes('/usecases/our-requests')) {
        return { data: data.ourRequests };
      } else if (url.includes('/usecases/promoter-submissions')) {
        return { data: data.promoterSubmissions };
      } else if (url.includes('/usecases/published')) {
        return { data: data.published };
      } else if (url.includes('/usecases/featured')) {
        return { data: data.featured };
      } else if (url.includes('/rewards/types')) {
        return { data: data.rewardTypes };
      } else if (url.includes('/rewards/global')) {
        return { data: data.globalRewards };
      } else if (url.includes('/rewards/specific')) {
        return { data: data.specificRewards };
      } else if (url.includes('/rewards/payments')) {
        return { data: data.payments };
      } else if (url.includes('/rewards/analytics')) {
        return { data: data.analytics };
      } else if (url.includes('/calls/our-requests')) {
        return { data: data.ourRequests };
      } else if (url.includes('/calls/promoter-submissions')) {
        return { data: data.promoterSubmissions };
      } else if (url.includes('/calls/activated')) {
        return { data: data.activated };
      }
      
      return { data };
    } catch (error) {
      console.error('Mock API Error:', error);
      throw error;
    }
  },
  
  post: async (url: string, data: any) => {
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));
    console.log('Mock POST:', url, data);
    return { data: { success: true, id: Date.now().toString() } };
  },
  
  put: async (url: string, data: any) => {
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));
    console.log('Mock PUT:', url, data);
    return { data: { success: true } };
  },
  
  patch: async (url: string, data: any) => {
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 500));
    console.log('Mock PATCH:', url, data);
    return { data: { success: true } };
  },
  
  delete: async (url: string) => {
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 500));
    console.log('Mock DELETE:', url);
    return { data: { success: true } };
  }
};

// Generic API hook
export const useApi = <T = any>(endpoint: string, dependencies: any[] = []) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await mockAPI.get(`/api${endpoint}`);
        setData(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, dependencies);

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await mockAPI.get(`/api${endpoint}`);
      setData(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, refetch };
};

// Specific hooks for each page
export const useDashboardData = () => {
  const { data: stats, loading: statsLoading } = useApi('/dashboard/stats');
  const { data: performance, loading: performanceLoading } = useApi('/dashboard/performance');
  const { data: topPerformers, loading: topPerformersLoading } = useApi('/dashboard/top-performers');
  const { data: rewardsByType, loading: rewardsByTypeLoading } = useApi('/dashboard/rewards-by-type');

  return {
    stats,
    performance,
    topPerformers,
    rewardsByType,
    loading: statsLoading || performanceLoading || topPerformersLoading || rewardsByTypeLoading
  };
};

export const usePromotersData = () => {
  const { data: promoters, loading: promotersLoading, refetch } = useApi('/promoters');
  const { data: stats, loading: statsLoading } = useApi('/promoters/stats');

  return {
    promoters: promoters || EMPTY_ARRAY,
    stats,
    loading: promotersLoading || statsLoading,
    refetch
  };
};

export const useProspectsData = () => {
  const { data: prospects, loading: prospectsLoading, refetch } = useApi('/prospects');
  const { data: stats, loading: statsLoading } = useApi('/prospects/stats');
  const { data: widgetSubmissions, loading: widgetLoading } = useApi('/prospects/widget-submissions');

  return {
    prospects: prospects || EMPTY_ARRAY,
    stats,
    widgetSubmissions: widgetSubmissions || EMPTY_ARRAY,
    loading: prospectsLoading || statsLoading || widgetLoading,
    refetch
  };
};

export const useMeetingsData = () => {
  const { data: meetings, loading, refetch } = useApi('/meetings');

  return {
    meetings: meetings || EMPTY_ARRAY,
    loading,
    refetch
  };
};

export const useReviewsData = () => {
  const { data: reviews, loading: reviewsLoading, refetch } = useApi('/reviews');
  const { data: external, loading: externalLoading } = useApi('/reviews/external');
  const { data: stats, loading: statsLoading } = useApi('/reviews/stats');

  return {
    reviews: reviews || EMPTY_ARRAY,
    external: external || EMPTY_ARRAY,
    stats,
    loading: reviewsLoading || externalLoading || statsLoading,
    refetch
  };
};

export const useTestimonialsData = () => {
  const { data: ourRequests, loading: ourRequestsLoading } = useApi('/testimonials/our-requests');
  const { data: promoterSubmissions, loading: submissionsLoading } = useApi('/testimonials/promoter-submissions');
  const { data: featured, loading: featuredLoading } = useApi('/testimonials/featured');

  return {
    ourRequests: ourRequests || EMPTY_ARRAY,
    promoterSubmissions: promoterSubmissions || EMPTY_ARRAY,
    featured: featured || EMPTY_ARRAY,
    loading: ourRequestsLoading || submissionsLoading || featuredLoading
  };
};

export const useUseCasesData = () => {
  const { data: ourRequests, loading: ourRequestsLoading } = useApi('/usecases/our-requests');
  const { data: promoterSubmissions, loading: submissionsLoading } = useApi('/usecases/promoter-submissions');
  const { data: published, loading: publishedLoading } = useApi('/usecases/published');
  const { data: featured, loading: featuredLoading } = useApi('/usecases/featured');

  return {
    ourRequests: ourRequests || EMPTY_ARRAY,
    promoterSubmissions: promoterSubmissions || EMPTY_ARRAY,
    published: published || EMPTY_ARRAY,
    featured: featured || EMPTY_ARRAY,
    loading: ourRequestsLoading || submissionsLoading || publishedLoading || featuredLoading
  };
};

export const useRewardsData = () => {
  const { data: rewardTypes, loading: typesLoading } = useApi('/rewards/types');
  const { data: globalRewards, loading: globalLoading } = useApi('/rewards/global');
  const { data: specificRewards, loading: specificLoading } = useApi('/rewards/specific');
  const { data: payments, loading: paymentsLoading } = useApi('/rewards/payments');
  const { data: analytics, loading: analyticsLoading } = useApi('/rewards/analytics');

  return {
    rewardTypes: rewardTypes || EMPTY_ARRAY,
    globalRewards: globalRewards || EMPTY_ARRAY,
    specificRewards: specificRewards || EMPTY_ARRAY,
    payments: payments || EMPTY_ARRAY,
    analytics,
    loading: typesLoading || globalLoading || specificLoading || paymentsLoading || analyticsLoading
  };
};

export const useCallsData = () => {
  const { data: ourRequests, loading: ourRequestsLoading } = useApi('/calls/our-requests');
  const { data: promoterSubmissions, loading: submissionsLoading } = useApi('/calls/promoter-submissions');
  const { data: activated, loading: activatedLoading } = useApi('/calls/activated');

  return {
    ourRequests: ourRequests || EMPTY_ARRAY,
    promoterSubmissions: promoterSubmissions || EMPTY_ARRAY,
    activated: activated || EMPTY_ARRAY,
    loading: ourRequestsLoading || submissionsLoading || activatedLoading
  };
};

export { mockAPI };